<template>
  <div class="column-view">
    <el-popover
      placement="bottom-start"
      trigger="click"
      popper-class="column-view-popper"
      v-model="visible"
      width="240"
    >
      <div class="cv-body">
        <div class="cv-header flex justify-between items-center h-52 px-20">
          <div class="flex items-center gap-12">
            <el-checkbox 
              v-model="selectAll" 
              @change="onSelectAllChange"
              class="select-all-checkbox"
            />
            <div class="text-14 font-light color-label">列展示</div>
          </div>
          <div @click="onReset" class="color-primary-light cursor-pointer text-14 font-light">重置</div>
        </div>
        <div class="cv-content overflow-y-auto">
          <!-- 固定在左侧 - 只有当有左侧固定列时才显示 -->
          <template v-if="fixedLeftList.length > 0">
            <div class="group-title h-32 f12 flex items-center pl-44 pr-16 box-sizing">固定在左侧</div>
            <div class="column-list">
              <div
                v-for="(item, index) in fixedLeftList"
                :key="item._key"
                class="column-item flex justify-between px-16 h-40"
                draggable="true"
                @dragstart="onDragStart($event, item)"
                @dragend="onDragEnd"
                @dragover="onDragOver($event, item)"
                @drop="onDrop($event, item)"
              >
                <div class="flex items-center gap-8 flex-1">
                  <div class="drag-handle flex items-center justify-center text-18 mr-8">
                    <img src="@/assets/view_icon.png" alt="拖拽" class="w-6 h-12" draggable="false">
                  </div>
                  <el-checkbox v-model="item.visible" @change="onCheckChange" />
                  <span class="cv-label ml-8 f14 color-label font-medium">{{ item.label }}</span>
                </div>
                <!-- 固定在左侧的操作按钮 -->
                <div class="ops flex items-center gap-12 cursor-pointer">
                  <el-tooltip content="不固定" placement="top">
                    <div class="switch-icon-btn" @click.stop="setFixed(item, null)">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 2L7 12M3 6L7 2L11 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M7 12L7 2M3 8L7 12L11 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                  </el-tooltip>
                  <el-tooltip content="切换到固定在右侧" placement="top">
                    <img src="@/assets/view_down.png" alt="固定在右侧" class="w-14 h-14 op-img" draggable="false" @click.stop="setFixed(item, 'right')">
                  </el-tooltip>
                </div>
              </div>
            </div>
          </template>

          <!-- 不固定 - 只有当有普通列时才显示 -->
          <template v-if="normalList.length > 0">
            <div class="group-title h-32 f12 flex items-center pl-44 pr-16 box-sizing">不固定</div>
            <div class="column-list">
              <div
                v-for="(item, index) in normalList"
                :key="item._key"
                class="column-item flex justify-between px-16 h-40"
                draggable="true"
                @dragstart="onDragStart($event, item)"
                @dragend="onDragEnd"
                @dragover="onDragOver($event, item)"
                @drop="onDrop($event, item)"
              >
                <div class="flex items-center gap-8 flex-1">
                  <div class="drag-handle flex items-center justify-center text-18 mr-8">
                    <img src="@/assets/view_icon.png" alt="拖拽" class="w-6 h-12" draggable="false">
                  </div>
                  <el-checkbox v-model="item.visible" @change="onCheckChange" />
                  <span class="cv-label ml-8 f14 color-label font-medium">{{ item.label }}</span>
                </div>
                <div class="ops ml-auto flex items-center gap-12 cursor-pointer">
                  <el-tooltip content="固定在列首" placement="top">
                    <img src="@/assets/view_up.png" alt="固定在列首" class="w-14 h-14 op-img" draggable="false" @click.stop="setFixed(item, 'left')">
                  </el-tooltip>
                  <el-tooltip content="固定在列尾" placement="top">
                    <img src="@/assets/view_down.png" alt="固定在列尾" class="w-14 h-14 op-img" draggable="false" @click.stop="setFixed(item, 'right')">
                  </el-tooltip>
                </div>
              </div>
            </div>
          </template>

          <!-- 固定在右侧 - 只有当有右侧固定列时才显示 -->
          <template v-if="fixedRightList.length > 0">
            <div class="group-title h-32 f12 flex items-center pl-44 pr-16 box-sizing">固定在右侧</div>
            <div class="column-list">
              <div
                v-for="(item, index) in fixedRightList"
                :key="item._key"
                class="column-item flex justify-between px-16 h-40"
                draggable="true"
                @dragstart="onDragStart($event, item)"
                @dragend="onDragEnd"
                @dragover="onDragOver($event, item)"
                @drop="onDrop($event, item)"
              >
                <div class="flex items-center gap-8 flex-1">
                  <div class="drag-handle flex items-center justify-center text-18 mr-8">
                    <img src="@/assets/view_icon.png" alt="拖拽" class="w-6 h-12" draggable="false">
                  </div>
                  <el-checkbox v-model="item.visible" @change="onCheckChange" />
                  <span class="cv-label ml-8 f14 color-label font-medium">{{ item.label }}</span>
                </div>
                <!-- 固定在右侧的操作按钮 -->
                <div class="ops flex items-center gap-12 cursor-pointer">
                  <el-tooltip content="切换到固定在左侧" placement="top">
                    <img src="@/assets/view_up.png" alt="固定在左侧" class="w-14 h-14 op-img" draggable="false" @click.stop="setFixed(item, 'left')">
                  </el-tooltip>
                  <el-tooltip content="切换到不固定" placement="top">
                    <div class="switch-icon-btn" @click.stop="setFixed(item, null)">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 2L7 12M3 6L7 2L11 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M7 12L7 2M3 8L7 12L11 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                  </el-tooltip>
                </div>
              </div>
            </div>
          </template>
        </div>        
      </div>

      <div class="cv-trigger flex items-center gap-4 cursor-pointer" slot="reference" type="text">
        <img src="@/assets/view.png" alt="视图" class="w-14 h-14">
        <span class="text-14 font-medium color-label cv-trigger-label">视图</span>
      </div>
    </el-popover>
  </div>
  
</template>

<script>
import { sk } from '@/config'
export default {
  name: 'ColumnView',
  props: {
    value: { // v-model columns 配置
      type: Array,
      default: () => []
    },
    // 重置基准
    defaultColumns: {
      type: Array,
      default: () => []
    },
    // 页面路径，用于生成缓存key
    pagePath: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      visible: false,
      treeData: this.cloneWithKeys(this.value.length ? this.value : this.defaultColumns),
      treeProps: {
        label: 'label',
        children: 'children'
      },
      checkedKeys: [],
      draggingKey: null,
      dragOverKey: null,
      selectAll: true
    }
  },
  computed: {
    fixedLeftList() {
      return this.treeData.filter(i => i.fixed === 'left')
    },
    fixedRightList() {
      return this.treeData.filter(i => i.fixed === 'right')
    },
    normalList() {
      return this.treeData.filter(i => !i.fixed)
    },
    // 生成缓存key（金河项目前缀，与山西项目隔离）
    cacheKey() {
      const userId = localStorage.getItem('userId') || 'default'
      const path = this.pagePath || this.$route?.path || 'default'
      return sk(`table_columns_${userId}_${path.replace(/\//g, '_')}`)
    }
  },
  watch: {
    value: {
      deep: true,
      handler(val) {
        // 外部更新时同步
        this.treeData = this.cloneWithKeys(val)
        this.$nextTick(() => {
          // 确保所有项的 visible 属性存在
          this.treeData.forEach(item => {
            if (item.visible === undefined) {
              item.visible = true
            }
          })
        })
      }
    }
  },
  methods: {
    // 从本地缓存读取列配置
    loadFromCache() {
      try {
        const cached = localStorage.getItem(this.cacheKey)
        if (cached) {
          const config = JSON.parse(cached)
          return config.columns || []
        }
      } catch (error) {
      }
      return null
    },
    
    // 保存列配置到本地缓存
    saveToCache(columns) {
      try {
        const userId = localStorage.getItem('userId') || 'default'
        const path = this.pagePath || this.$route?.path || 'default'
        const convertedColumns = this.convertToCacheFormat(columns)
        const config = {
          userId,
          pagePath: path,
          columns: convertedColumns,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        localStorage.setItem(this.cacheKey, JSON.stringify(config))
      } catch (error) {
        console.error('保存列配置缓存失败:', error)
      }
    },
    
    // 转换为缓存格式（兼容您提供的JSON结构）
    convertToCacheFormat(columns) {
      return columns.map((col, index) => ({
        id: col.prop || col.id || `col_${index}`,
        name: col.label,
        width: col.width || 100,
        minWidth: col.minWidth || col.width || 100, // 保存minWidth，优先使用minWidth
        visible: col.visible !== false,
        order: index,
        pinLeft: col.fixed === 'left',
        pinRight: col.fixed === 'right',
        canHide: col.canHide !== false,
        canDrag: col.canDrag !== false,
        flex: col.flex || 1
      }))
    },
    
    // 从缓存格式转换为组件格式
    convertFromCacheFormat(cachedColumns) {
      return cachedColumns.map(col => ({
        prop: col.id,
        label: col.name,
        width: col.width,
        minWidth: col.minWidth || col.width, // 恢复minWidth
        visible: col.visible,
        fixed: col.pinLeft ? 'left' : (col.pinRight ? 'right' : null),
        canHide: col.canHide,
        canDrag: col.canDrag,
        flex: col.flex
      }))
    },

    // 保留用户已有的列顺序和显隐设置，同时补入后续版本新增的默认列。
    mergeCachedColumnsWithDefaults(cachedColumns) {
      const merged = Array.isArray(cachedColumns) ? cachedColumns.map(item => ({ ...item })) : []
      const existingProps = new Set(merged.map(item => item && item.prop).filter(Boolean))
      ;(this.defaultColumns || []).forEach(item => {
        if (!item || !item.prop || existingProps.has(item.prop)) return
        merged.push({ ...item, visible: item.visible !== false })
        existingProps.add(item.prop)
      })
      return merged
    },
    
    // 给每个节点加唯一 key，并保证 visible 字段存在
    cloneWithKeys(list, prefix = '') {
      const walk = (arr, p = prefix) => (arr || []).map((item, idx) => {
        const node = {
          ...item,
          visible: item.visible !== false,
          fixed: item.fixed || null,
          _key: item._key || `${p}${idx}-${item.prop || item.label}`
        }
        if (item.children && item.children.length) {
          node.children = walk(item.children, node._key + '-')
        }
        return node
      })
      return JSON.parse(JSON.stringify(walk(list)))
    },
    // Tree 勾选联动到 visible 字段
    onCheckChange() {
      // 更新全选状态
      this.updateSelectAllState()
      // 即时同步更改
      this.emitChange()
    },
    onSelectAllChange(checked) {
      // 全选/取消全选所有项
      this.treeData.forEach(item => {
        item.visible = checked
      })
      this.emitChange()
    },
    updateSelectAllState() {
      // 根据当前选中状态更新全选复选框
      const visibleCount = this.treeData.filter(item => item.visible !== false).length
      this.selectAll = visibleCount === this.treeData.length
    },
    onDragStart(event, item) {
      this.draggingKey = item._key
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', item._key)
      event.target.style.opacity = '0.5'
    },
    onDragEnd(event) {
      this.draggingKey = null
      this.dragOverKey = null
      event.target.style.opacity = '1'
    },
    onDragOver(event, item) {
      event.preventDefault()
      this.dragOverKey = item._key
    },
    onDrop(event, item) {
      event.preventDefault()
      const fromKey = this.draggingKey
      const toKey = item._key
      if (!fromKey || fromKey === toKey) return

      const list = [...this.treeData]
      const fromIndex = list.findIndex(n => n._key === fromKey)
      const toIndex = list.findIndex(n => n._key === toKey)
      if (fromIndex === -1 || toIndex === -1) return

      // 若两个项的 fixed 不同，允许跨组拖拽并继承目标组 fixed
      if (list[fromIndex].fixed !== list[toIndex].fixed) {
        list[fromIndex].fixed = list[toIndex].fixed || null
      }

      const dragged = list.splice(fromIndex, 1)[0]
      const insertIndex = fromIndex < toIndex ? toIndex : toIndex
      list.splice(insertIndex, 0, dragged)
      this.treeData = list
      this.$nextTick(this.emitChange)
    },
    onReset() {
      // 优先使用传入的 defaultColumns，确保重置到正确的默认状态
      const resetColumns = this.defaultColumns && this.defaultColumns.length > 0 ? this.defaultColumns : this.value
      this.treeData = this.cloneWithKeys(resetColumns)
      // 保留默认列的显隐设置；未配置 visible 的列默认勾选
      this.treeData.forEach(item => {
        item.visible = item.visible !== false
        item.fixed = item.fixed || null
      })
      this.selectAll = this.treeData.every(item => item.visible !== false)
      // 清除缓存
      localStorage.removeItem(this.cacheKey)
      this.$nextTick(() => {
        this.emitChange()
      })
    },
    // 顺序通过拖拽，按钮仅用于固定
    setFixed(item, pos) {
      const i = this.treeData.findIndex(n => n._key === item._key)
      if (i !== -1) {
        this.$set(this.treeData[i], 'fixed', pos)
        this.emitChange()
      }
    },
    emitChange() {
      const result = this.exportColumns()
      
      // 保存到本地缓存
      this.saveToCache(result)
      this.$emit('input', result)
      this.$emit('change', result)
      
      // 检查是否有可见的数据列（排除序号、操作、复选框列）
      const hasVisibleDataColumns = result.some(col => 
        col.visible !== false && 
        col.prop !== 'selection' && 
        col.prop !== 'index' && 
        col.prop !== 'action' &&
        col.label !== '序号' && 
        col.label !== '操作' &&
        col.type !== 'selection' &&
        col.type !== 'index' &&
        col.type !== 'action'
      )
      
      // 发出空状态事件 - 只有当没有可见的数据列时才显示空状态
      this.$emit('empty-state', !hasVisibleDataColumns)
      
      // 发出填满页面事件 - 当只有序号、操作、复选框列时
      this.$emit('fill-page', !hasVisibleDataColumns)
    },
    
    // 公共方法：手动保存当前配置到缓存
    saveCurrentConfig() {
      // 从父组件的 value 同步最新数据到 treeData
      this.treeData = this.cloneWithKeys(this.value)
      const result = this.exportColumns()
      this.saveToCache(result)
    },
    // 导出列配置（树结构）
    exportColumns() {
      const strip = arr => arr.map(n => {
        const item = { 
          label: n.label, 
          prop: n.prop, 
          width: n.width, 
          minWidth: n.minWidth,
          visible: n.visible,
          fixed: n.fixed || null
        }
        if (n.children && n.children.length) {
          item.children = strip(n.children)
        }
        return item
      })
      return strip(this.treeData)
    },
    // 返回扁平可渲染列（仅 visible）
    getVisibleColumns() {
      const flat = []
      const walk = arr => arr.forEach(n => {
        if (n.children && n.children.length) {
          const children = n.children.filter(c => c.visible)
          if (n.visible !== false && children.length) {
            flat.push({ label: n.label, children: [] })
            const container = flat[flat.length - 1]
            const startLen = flat.length
            walk(children)
            // 将刚刚 push 的子列归入 children
            const appended = flat.splice(startLen - 0)
            container.children = appended
            flat.push(container)
          } else if (children.length) {
            walk(children)
          }
        } else if (n.visible !== false) {
          flat.push({ label: n.label, prop: n.prop, width: n.width })
        }
      })
      walk(this.treeData)
      return flat
    },
    countChecked(node) {
      const children = node.children || []
      return children.filter(c => c.visible !== false).length
    }
  },
  mounted() {
    // 尝试从缓存读取配置
    const cachedColumns = this.loadFromCache()
    if (cachedColumns && cachedColumns.length > 0) {
      // 从缓存恢复配置
      const convertedColumns = this.convertFromCacheFormat(cachedColumns)
      this.treeData = this.cloneWithKeys(this.mergeCachedColumnsWithDefaults(convertedColumns))
    } else {
      // 使用默认配置
      this.treeData = this.cloneWithKeys(this.value.length ? this.value : this.defaultColumns)
    }
    
    // 初始化可见状态
    this.treeData.forEach(item => {
      if (item.visible === undefined) {
        item.visible = true
      }
      if (item.fixed === undefined) {
        item.fixed = null
      }
    })
    // 初始化全选状态
    this.updateSelectAllState()
    
    // 无论是否有缓存，都同步初始配置到父组件
    this.$nextTick(() => {
      this.emitChange()
    })
  }
}
</script>

<style scoped>
.group-title {
  color: rgba(144, 147, 153, 1);
}
.column-item {
  transition: all 0.2s;
  cursor: move;
  user-select: none;
}
.column-item:hover { background: rgba(42, 117, 247, 0.08); border-radius: 6px; }
.column-item .ops { opacity: 0; transition: opacity .15s ease; }
.column-item:hover .ops { opacity: 1; }
/* 触发按钮悬停态：图片与文字颜色联动 */
.cv-trigger:hover img { filter: brightness(0) saturate(100%) invert(36%) sepia(86%) saturate(1853%) hue-rotate(205deg) brightness(97%) contrast(96%); }
.cv-trigger:hover .cv-trigger-label { color: rgba(42, 117, 247, 1); }
.column-item.is-dragging {
  background: #e6f7ff;
  border: 1px dashed #409eff;
  opacity: 0.8;
}
.column-item.drag-over {
  background: #e6f7ff;
  border: 1px dashed #409eff;
  transform: scale(1.01);
  position: relative;
}

.column-item.drag-over::before {
  content: '';
  position: absolute;
  top: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: #409eff;
  border-radius: 2px;
  box-shadow: 0 0 4px rgba(64, 158, 255, 0.3);
}
.drag-handle:hover {
  color: #409eff;
  background: #e6f7ff;
  border-color: #b3d8ff;
  transform: scale(1.05);
}
.drag-handle:active {
  transform: scale(0.95);
  background: #d6e4ff;
}

/* 复选框样式优化 */
.column-view-popper .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: rgba(42, 117, 247, 1);
  border-color: rgba(42, 117, 247, 1);
  width: 16px !important;
  height: 16px !important;
}
.el-checkbox__inner{
  width: 16px !important;
  height: 16px !important;
}
/* 全选复选框样式 */
.select-all-checkbox {
  transform: scale(1.1);
}

/* 普通复选框样式 - 更大 */
.column-view-popper .el-checkbox {
  transform: scale(1.1);
}

.column-view-popper .el-checkbox__inner {
  width: 16px !important;
  height: 16px !important;
}

.column-view-popper .el-checkbox__inner::after {
  width: 4px;
  height: 7px;
  left: 50% !important;
  top: 50% !important;
  margin-left: -2px;
  margin-top: -4px;
  border: 2px solid #fff;
  border-left: 0;
  border-top: 0;
  transform: rotate(45deg);
}

/* 切换图标按钮样式 */
.switch-icon-btn {
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #409eff;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  border-radius: 2px;
}
</style>

<style>
/* 全局样式 - 用于 popper */
.column-view-popper { 
  padding: 0 !important; 
  border-radius: 6px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  overflow: hidden !important;
}

/* 内容滚动区域 */
.cv-content {
  max-height: 560px;
  overflow-y: auto;
}

</style>


